/**
 * Copyright (C) 2023 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */

import { Organization } from '../../shared/entities/organization';
import { Action, ActionCreators } from './actions';

export interface State {
    readonly organizations: Organization[] | undefined;
    readonly isRequestingOrganizations: boolean;
    readonly isOrganizationsRequestFinished: boolean;
    readonly organizationsRequestError?: string;

    readonly currentOrganization: Organization | undefined;
    readonly isRequestingCurrentOrganization: boolean;
    readonly isCurrentOrganizationRequestFinished: boolean;
    readonly currentOrganizationRequestError?: string;

    readonly isProvisioningOrganization: boolean;
    readonly isOrganizationProvisioningFinished: boolean;
    readonly organizationProvisioningError?: string;

    readonly isDeletingOrganization: boolean;
    readonly isDeletingOrganizationFinished: boolean;
    readonly deleteOrganizationError?: string;

    readonly isUpdatingOrganization: boolean;
    readonly isUpdatingOrganizationFinished: boolean;
    readonly updateOrganizationError?: string;

    readonly isRequestingOrganizationTerminals: boolean;
    readonly isOrganizationTerminalsRequestFinished: boolean;
    readonly organizationTerminalsRequestError?: string;
}

export const initialOrganizationProvisioningState: State = {
    organizations: undefined,
    isRequestingOrganizations: false,
    isOrganizationsRequestFinished: false,
    organizationsRequestError: undefined,
    
    currentOrganization: undefined,
    isRequestingCurrentOrganization: false,
    isCurrentOrganizationRequestFinished: false,
    currentOrganizationRequestError: undefined,

    isProvisioningOrganization: false,
    isOrganizationProvisioningFinished: false,
    organizationProvisioningError: undefined,

    isDeletingOrganization: false,
    isDeletingOrganizationFinished: false,
    deleteOrganizationError: undefined,

    isUpdatingOrganization: false,
    isUpdatingOrganizationFinished: false,
    updateOrganizationError: undefined,

    isRequestingOrganizationTerminals: false,
    isOrganizationTerminalsRequestFinished: false,
    organizationTerminalsRequestError: undefined,
};

export default function reducer(state: State = initialOrganizationProvisioningState, action: Action): State {
  let partialState: Partial<State> | undefined;

  switch (action.type) {
    //For requesting all orgs
    case ActionCreators.RequestOrganizations.type:
        partialState = {
            organizations: undefined,
            isRequestingOrganizations: true,
            isOrganizationsRequestFinished: false,
            organizationsRequestError: undefined,
        };
        break;

    case ActionCreators.ReceivedOrganizationsSuccess.type: {
        partialState = {
            organizations: action.payload.response,
            isRequestingOrganizations: false,
            isOrganizationsRequestFinished: true,
            organizationsRequestError: undefined,
        };
        break;
      }

    case ActionCreators.ReceivedOrganizationsError.type:
        partialState = {
            isRequestingOrganizations: false,
            isOrganizationsRequestFinished: true,
            organizationsRequestError: action.payload.message,
        };
        break;

    case ActionCreators.ClearReceivedOrganizationsError.type:
        partialState = {
            organizationsRequestError: undefined,
        };
        break;
    
    //For requesting specific org
    case ActionCreators.RequestCurrentOrganization.type:
        partialState = {
            currentOrganization: undefined,
            isRequestingCurrentOrganization: true,
            isCurrentOrganizationRequestFinished: false,
            currentOrganizationRequestError: undefined,
        };
        break;

    case ActionCreators.ReceivedCurrentOrganizationSuccess.type: {
        partialState = {
            currentOrganization: action.payload.response,
            isRequestingCurrentOrganization: false,
            isCurrentOrganizationRequestFinished: true,
            currentOrganizationRequestError: undefined,
        };
        break;
    }

    case ActionCreators.ReceivedCurrentOrganizationError.type:
        partialState = {
            isRequestingCurrentOrganization: false,
            isCurrentOrganizationRequestFinished: true,
            currentOrganizationRequestError: action.payload.message,
        };
        break;
    
    case ActionCreators.ClearReceivedCurrentOrganizationError.type:
        partialState = {
            currentOrganizationRequestError: undefined,
        };
        break;
    
    //For adding an org
    case ActionCreators.ProvisionOrganization.type:
        partialState = {
            isProvisioningOrganization: true,
            isOrganizationProvisioningFinished: false,
            organizationProvisioningError: undefined,
        };
        break;

    case ActionCreators.OrganizationProvisioningSuccess.type:
        partialState = {
            isProvisioningOrganization: false,
            isOrganizationProvisioningFinished: true,
            organizationProvisioningError: undefined,
            currentOrganization: action.payload,
        };
        break;

    case ActionCreators.OrganizationProvisioningError.type:
        partialState = {
            isProvisioningOrganization: false,
            isOrganizationProvisioningFinished: true,
            organizationProvisioningError: action.payload.message,
        };
        break;
        
    case ActionCreators.ClearOrganizationProvisioningError.type:
        partialState = {
            organizationProvisioningError: undefined,
        };
        break;

    //For deleting an org
    case ActionCreators.DeleteOrganization.type:
        partialState = {
            isDeletingOrganization: true,
            isDeletingOrganizationFinished: false,
            deleteOrganizationError: undefined,
        };
        break;

    case ActionCreators.DeleteOrganizationSuccess.type:
        partialState = {
            isDeletingOrganization: false,
            isDeletingOrganizationFinished: true,
            deleteOrganizationError: undefined,
        };
        break;

    case ActionCreators.DeleteOrganizationError.type:
        partialState = {
            isDeletingOrganization: false,
            isDeletingOrganizationFinished: true,
            deleteOrganizationError: action.payload.message,
        };
        break;

    case ActionCreators.ClearDeleteOrganizationError.type:
        partialState = {
            deleteOrganizationError: undefined,
        };
        break;

    //For updating an org
    case ActionCreators.UpdateOrganization.type:
        partialState = {
            isUpdatingOrganization: true,
            isUpdatingOrganizationFinished: false,
            updateOrganizationError: undefined,
        };
        break;

    case ActionCreators.UpdateOrganizationSuccess.type:
        partialState = {
            isUpdatingOrganization: false,
            isUpdatingOrganizationFinished: true,
            updateOrganizationError: undefined,
            currentOrganization: action.payload,
        };
        break;

    case ActionCreators.UpdateOrganizationError.type:
        partialState = {
            isUpdatingOrganization: false,
            isUpdatingOrganizationFinished: true,
            updateOrganizationError: action.payload.message,
        };
        break;

    case ActionCreators.ClearUpdateOrganizationError.type:
        partialState = {
            updateOrganizationError: undefined,
        };
        break;
    
    //For requesting specific org
    case ActionCreators.RequestOrganizationTerminals.type:
        partialState = {
            currentOrganization: undefined,
            isRequestingOrganizationTerminals: true,
            isOrganizationTerminalsRequestFinished: false,
            organizationTerminalsRequestError: undefined,
        };
        break;

    case ActionCreators.ReceivedOrganizationTerminalsSuccess.type: {
        partialState = {
            currentOrganization: action.payload.response,
            isRequestingOrganizationTerminals: false,
            isOrganizationTerminalsRequestFinished: true,
            organizationTerminalsRequestError: undefined,
        };
        break;
    }

    case ActionCreators.ReceivedOrganizationTerminalsError.type:
        partialState = {
            isRequestingOrganizationTerminals: false,
            isOrganizationTerminalsRequestFinished: true,
            organizationTerminalsRequestError: action.payload.message,
        };
        break;
    
    case ActionCreators.ClearReceivedOrganizationTerminalsError.type:
        partialState = {
            organizationTerminalsRequestError: undefined,
        };
        break;
    
    
    default:
      return state;
  }
  return { ...state, ...partialState };
}